import { Document } from '@app/modules/core/store/models';
import { GeometryTypes, LayerMapFeatures, LayerRenderer, MapDrawingContext } from '../models';
import { HeatmapOptions, Layer, RenderLensMode } from '../../store/cop/models';
import { FeatureRenderingUtilityService } from '../features/feature-rendering-utility.service';
import { HeatmapGradientMap, HeatmapGradientType } from '../visualizations/heatmap';

interface HeatmapLayerRef {
  documentData: Record<string, google.maps.LatLng[]>;
  heatmap: google.maps.visualization.HeatmapLayer;
  renderLensMode: RenderLensMode;
}

export class VisualizationsHeatmapLayerRenderer implements LayerRenderer {
  private layerRefs: Record<string, HeatmapLayerRef> = {};

  ensureLayerRef(drawingContext: MapDrawingContext, layer: Layer): void {
    if (!Boolean(this.layerRefs[layer.uId])) {
      this.layerRefs[layer.uId] = {
        documentData: {},
        heatmap: this.generateHeatmap(layer, drawingContext),
        renderLensMode: layer.displayOptions.renderLensMode ?? RenderLensMode.default
      };
    } else if (this.layerRefs[layer.uId].heatmap.getMap() !== drawingContext.googleMap) {
      this.layerRefs[layer.uId].heatmap.setMap(drawingContext.googleMap);
    } else {
      return;
    }

    drawingContext.onLoadingDone(layer);
  }

  addDocuments(drawingContext: MapDrawingContext, layer: Layer, documents: Document[]): void {
    this.ensureLayerRef(drawingContext, layer);
    this.processDocumentDataForLayer(drawingContext, layer, documents);
    this.render(layer);
  }

  removeDocuments(drawingContext: MapDrawingContext, layer: Layer, documentUIds: string[] = []): void {
    const layerRef = this.layerRefs[layer.uId];

    if (!documentUIds.length || !Boolean(layerRef)) {
      return;
    }

    const { documentData } = layerRef;

    documentUIds.forEach(docUId => {
      delete documentData[docUId];
    });

    this.render(layer);
  }

  updateDocuments(drawingContext: MapDrawingContext, layer: Layer, documents: Document[]): void {
    // if Visualization Layer has the viewport render lens mode enabled (Items in View), addDocuments does not trigger for layer
    // this ensures there is a HeatmapLayerRef for layers with that setting enabled.
    this.ensureLayerRef(drawingContext, layer);
    this.processDocumentDataForLayer(drawingContext, layer, documents);
    this.render(layer);
  }

  removeDocument(drawingContext: MapDrawingContext, layer: Layer, uid: string, layerFeatures: LayerMapFeatures): void {
    const layerRef = this.layerRefs[layer.uId];

    if (!Boolean(layerRef)) {
      return;
    }

    const { documentData } = layerRef;

    delete documentData[uid];

    this.render(layer);
  }

  private getGeoDataForFeature(feature: any): google.maps.LatLng[] {
    const geometry = feature?.geometry ?? feature ?? {};

    const { type = null, coordinates = [] } = geometry;

    if (!Boolean(type) || !Boolean(coordinates.length)) {
      return [];
    }

    switch (type) {
      case GeometryTypes.point:
        const [lng, lat] = coordinates;
        return [new google.maps.LatLng({ lat, lng })];

      case GeometryTypes.polygon:
      case GeometryTypes.multiPolygon:
        // util service only returns 2d array if data exists
        const coords = FeatureRenderingUtilityService.normalizePolygonCoordinates(coordinates);

        if (!coords.length) {
          return [];
        }

        return (coords as google.maps.LatLng[][]).reduce((acc, c = []) => {
          if (c.length) {
            const bounds = new google.maps.LatLngBounds();
            c.forEach(latLng => bounds.extend(latLng));
            acc.push(bounds.getCenter());
          }

          return acc;
        }, []);

      case GeometryTypes.lineString:
      case GeometryTypes.multiLineString:
        const bounds = new google.maps.LatLngBounds();
        coordinates.forEach(([lng, lat]) => bounds.extend(new google.maps.LatLng({ lat, lng })));
        return [bounds.getCenter()];

      case GeometryTypes.feature:
        return this.getGeoDataForFeature(geometry);

      default:
        return [];
    }
  }

  private getHeatmapLayerOptions(layer: Layer): google.maps.visualization.HeatmapLayerOptions {
    const { visualizationLayerOptions = {} } = layer.displayOptions ?? {};

    const {
      heatmapGradientType = HeatmapGradientType.Default,
      heatmapOpacity: opacity = null,
      heatmapRadius: radius = null
    } = visualizationLayerOptions as HeatmapOptions;

    const gradient = HeatmapGradientMap[heatmapGradientType];

    const options: google.maps.visualization.HeatmapLayerOptions = {
      gradient,
      opacity,
      radius
    };

    return options;
  }

  private generateHeatmap(layer: Layer, { googleMap: map }: MapDrawingContext): google.maps.visualization.HeatmapLayer {
    const options = this.getHeatmapLayerOptions(layer);

    const heatmapLayer = new google.maps.visualization.HeatmapLayer({
      ...options,
      map
    });

    return heatmapLayer;
  }

  private render(layer: Layer): void {
    const layerRef = this.layerRefs[layer.uId];

    if (!Boolean(layerRef)) {
      console.error('No Heatmap Layer reference found');
      return;
    }

    const { documentData, heatmap } = layerRef;
    const data = Object.values(documentData).reduce((acc, docData) => [...acc, ...docData], []);
    const options = this.getHeatmapLayerOptions(layer);

    heatmap.setOptions({
      ...options,
      data
    });
  }

  private processDocumentDataForLayer(drawingContext: MapDrawingContext, layer: Layer, documents: Document[]): void {
    const layerRef = this.layerRefs[layer.uId];

    if (!Boolean(layerRef)) {
      console.error('No Heatmap Layer reference found');
      return;
    }

    const { documentData, renderLensMode } = layerRef;

    if (renderLensMode === RenderLensMode.viewport) {
      // clear existing data when rendering mode is viewport (Display: Items in View)
      // !! this needs to be referential, do no make copy of documentData object !!
      Object.keys(documentData).forEach(key => delete documentData[key]);
    }

    const dataset = drawingContext.mapState.datasetsSet[layer.datasetUId];

    documents.forEach(document => {
      const geoFeatures = FeatureRenderingUtilityService.getGeometryFeaturesForDisplay(dataset, layer, document);

      if (!Boolean(geoFeatures)) {
        return;
      }

      const data = geoFeatures.reduce((acc, feature) => [...acc, ...this.getGeoDataForFeature(feature)], []);
      documentData[document.uid] = data;
    });
  }
}
